// src/components/Manager/ComptesRendusPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Plus,
  Edit,
  Eye,
  Download,
  Filter,
  Search,
  CheckCircle,
  AlertCircle,
  Archive,
  Trash2,
  X,
  Clock,
} from 'lucide-react';
import Layout from '../Layout';

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://") ? rawBackendUrl : `https://${rawBackendUrl}`;
const API_URL = `${backend_url}/api`;
const getToken = () => localStorage.getItem('access_token');

export default function ComptesRendusPage() {
  const navigate = useNavigate();
  const [comptesRendus, setComptesRendus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    brouillons: 0,
    publies: 0,
    archives: 0
  });
  const [filters, setFilters] = useState({
    statut: 'tous',
    recherche: ''
  });
  
  // États pour le popup d'ajout
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [reunions, setReunions] = useState([]);
  const [selectedReunion, setSelectedReunion] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loadingReunions, setLoadingReunions] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  
  // État pour le popup de confirmation de suppression
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [compteRenduToDelete, setCompteRenduToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  useEffect(() => {
    fetchComptesRendus();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchComptesRendus();
  }, [filters]);

  const fetchComptesRendus = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      if (!token) {
        console.error("Token manquant");
        setError("Vous devez être connecté");
        setLoading(false);
        return;
      }

      let url = `${API_URL}/comptes-rendus/`;
      const params = new URLSearchParams();
      
      if (filters.statut && filters.statut !== 'tous') {
        params.append('statut', filters.statut);
      }
      if (filters.recherche) {
        params.append('search', filters.recherche);
      }
      
      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const crList = data.results || data;
        setComptesRendus(crList);
      } else if (response.status === 401) {
        console.error("Non autorisé - token invalide");
        setError("Session expirée, veuillez vous reconnecter");
      } else {
        console.error("Erreur chargement CR:", response.status);
        setError("Erreur lors du chargement des comptes rendus");
      }
    } catch (error) {
      console.error("Erreur chargement CR:", error);
      setError("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/comptes-rendus/stats/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Erreur stats:", error);
    }
  };

  const fetchReunions = async () => {
    setLoadingReunions(true);
    setUploadError(null);
    try {
      const token = getToken();
      
      if (!token) {
        console.error("Token manquant");
        setLoadingReunions(false);
        return;
      }

      const url = `${API_URL}/reunions/`;
      
      const response = await fetch(url, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        let reunionsList = [];
        
        if (Array.isArray(data)) {
          reunionsList = data;
        } else if (data.results && Array.isArray(data.results)) {
          reunionsList = data.results;
        }
        
        const formattedReunions = reunionsList.map(reunion => ({
          id: reunion.id,
          titre: reunion.titre || reunion.title || "Sans titre",
          date_heure: reunion.date_heure || reunion.date || null
        }));
        
        setReunions(formattedReunions);
      } else {
        setReunions([]);
      }
    } catch (error) {
      console.error("Erreur lors du chargement:", error);
      setReunions([]);
    } finally {
      setLoadingReunions(false);
    }
  };

  const handleOpenAddPopup = () => {
    fetchReunions();
    setShowAddPopup(true);
    setSelectedReunion('');
    setPdfFile(null);
    setUploadSuccess(false);
    setUploadError(null);
  };

  const handleView = async (crId) => {
    try {
      const token = getToken();
      if (!token) {
        console.error("Vous devez être connecté");
        return;
      }
      
      console.log("Affichage du CR:", crId);
      
      const pdfUrl = `${API_URL}/comptes-rendus/${crId}/pdf/?token=${token}`;
      
      const newWindow = window.open();
      if (newWindow) {
        newWindow.location.href = pdfUrl;
      } else {
        window.location.href = pdfUrl;
      }
      
    } catch (error) {
      console.error("Erreur affichage:", error);
    }
  };

  const handleDownload = async (crId) => {
    try {
      const token = getToken();
      if (!token) {
        console.error("Vous devez être connecté");
        setError("Vous devez être connecté");
        return;
      }
      
      console.log("Téléchargement du CR:", crId);
      
      const downloadUrl = `${API_URL}/comptes-rendus/${crId}/download/?token=${token}`;
      
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Erreur réponse:", response.status, errorText);
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const blob = await response.blob();
      console.log("Taille du blob:", blob.size, "bytes");
      console.log("Type du blob:", blob.type);
      
      if (blob.size === 0) {
        throw new Error("Le fichier téléchargé est vide");
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `compte_rendu_${crId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log("Téléchargement PDF réussi");
      
      const successMsg = document.createElement('div');
      successMsg.style.cssText = 'position:fixed;bottom:20px;right:20px;background:#10b981;color:white;padding:12px 20px;border-radius:8px;z-index:9999';
      successMsg.textContent = 'Téléchargement terminé !';
      document.body.appendChild(successMsg);
      setTimeout(() => successMsg.remove(), 3000);
      
    } catch (error) {
      console.error("Erreur téléchargement:", error);
      setError(`Erreur: ${error.message}`);
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleDelete = (compteRendu) => {
    setCompteRenduToDelete(compteRendu);
    setShowDeletePopup(true);
  };

  const confirmDelete = async () => {
    if (!compteRenduToDelete) return;
    
    setDeleting(true);
    setDeleteSuccess(false);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/comptes-rendus/${compteRenduToDelete.id}/delete/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setComptesRendus(comptesRendus.filter(cr => cr.id !== compteRenduToDelete.id));
        setShowDeletePopup(false);
        setCompteRenduToDelete(null);
        setDeleteSuccess(true);
        fetchStats();
        
        setTimeout(() => setDeleteSuccess(false), 3000);
      } else {
        console.error("Erreur suppression:", response.status);
      }
    } catch (error) {
      console.error("Erreur suppression:", error);
    } finally {
      setDeleting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setUploadError(null);
    if (file) {
      if (file.type !== 'application/pdf') {
        console.error("Veuillez sélectionner un fichier PDF");
        setUploadError("Le fichier doit être au format PDF");
        e.target.value = null;
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        console.error("Fichier trop volumineux");
        setUploadError("Le fichier ne doit pas dépasser 10 Mo");
        e.target.value = null;
        return;
      }
      setPdfFile(file);
    }
  };

  const handleUploadCompteRendu = async () => {
    if (!selectedReunion) {
      setUploadError("Veuillez sélectionner une réunion");
      return;
    }

    if (!pdfFile) {
      setUploadError("Veuillez sélectionner un fichier PDF");
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(false);
    
    try {
      const token = getToken();
      
      const formData = new FormData();
      formData.append('reunion', selectedReunion);
      formData.append('statut', 'publie');
      
      const resume = `Compte rendu uploadé - ${pdfFile.name.replace('.pdf', '')}`;
      formData.append('resume', resume);
      formData.append('fichier_pdf', pdfFile);

      console.log("Uploading:", {
        reunion: selectedReunion,
        fileName: pdfFile.name,
        fileSize: pdfFile.size
      });

      const response = await fetch(`${API_URL}/comptes-rendus/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const newCompteRendu = await response.json();
        console.log("Compte rendu créé:", newCompteRendu);
        
        setUploadSuccess(true);
        
        setTimeout(() => {
          setShowAddPopup(false);
          setSelectedReunion('');
          setPdfFile(null);
          setUploadSuccess(false);
          fetchComptesRendus();
          fetchStats();
        }, 1500);
      } else {
        const error = await response.json();
        console.error("Erreur réponse:", error);
        setUploadError(error.error || "Erreur lors de l'upload");
      }
    } catch (error) {
      console.error("Erreur upload:", error);
      setUploadError("Erreur de connexion au serveur");
    } finally {
      setUploading(false);
    }
  };

  const getStatusStyle = (statut) => {
    const styles = {
      brouillon: { bg: '#f3f4f6', color: '#4b5563', icon: <Clock size={12} />, text: 'Brouillon' },
      publie: { bg: '#dbeafe', color: '#2563eb', icon: <CheckCircle size={12} />, text: 'Publié' },
      archive: { bg: '#e5e7eb', color: '#6b7280', icon: <Archive size={12} />, text: 'Archivé' }
    };
    return styles[statut] || styles.publie;
  };

  return (
    <Layout>
      <div style={styles.page}>
        <h1 style={styles.title}>COMPTES RENDUS</h1>
        <p style={styles.subtitle}>
          Consultez et gérez tous les comptes rendus de réunions
        </p>

        {deleteSuccess && (
          <div style={styles.successMessage}>
            <CheckCircle size={20} color="#10b981" />
            <span>Compte rendu supprimé avec succès !</span>
          </div>
        )}

        <div style={styles.statsBar}>
          <div style={styles.statItem}>
            <span style={styles.statValue}>{stats.total}</span>
            <span style={styles.statLabel}>Total</span>
          </div>
          <div style={styles.statItem}>
            <span style={{...styles.statValue}}>{stats.brouillons}</span>
            <span style={styles.statLabel}>Brouillons</span>
          </div>
          <div style={styles.statItem}>
            <span style={{...styles.statValue}}>{stats.publies}</span>
            <span style={styles.statLabel}>Publiés</span>
          </div>
          <div style={styles.statItem}>
            <span style={{...styles.statValue}}>{stats.archives}</span>
            <span style={styles.statLabel}>Archivés</span>
          </div>
        </div>

        <div style={styles.topBar}>
          <div style={styles.filterGroup}>
            <Filter size={18} color="#666" />
            <select
              value={filters.statut}
              onChange={(e) => setFilters({...filters, statut: e.target.value})}
              style={styles.filterSelect}
            >
              <option value="tous">Tous les statuts</option>
              <option value="brouillon">Brouillons</option>
              <option value="publie">Publiés</option>
              <option value="archive">Archivés</option>
            </select>
          </div>

          <div style={styles.searchBox}>
            <Search size={16} color="#666" />
            <input
              type="text"
              placeholder="Rechercher un compte rendu..."
              value={filters.recherche}
              onChange={(e) => setFilters({...filters, recherche: e.target.value})}
              style={styles.searchInput}
            />
          </div>

          <button style={styles.addBtn} onClick={handleOpenAddPopup}>
            <Plus size={18} /> Uploader un compte rendu
          </button>
        </div>

        {error && (
          <div style={styles.errorMessage}>
            <AlertCircle size={20} color="#ef4444" />
            <span>{error}</span>
            <button onClick={fetchComptesRendus} style={styles.retryButton}>Réessayer</button>
          </div>
        )}

        {loading ? (
          <div style={styles.loading}>Chargement...</div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Titre</th>
                  <th style={styles.th}>Réunion</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Auteur</th>
                  <th style={styles.th}>Statut</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {comptesRendus.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={styles.emptyRow}>
                      Aucun compte rendu trouvé
                    </td>
                  </tr>
                ) : (
                  comptesRendus.map((cr) => {
                    const status = getStatusStyle(cr.statut);
                    
                    return (
                      <tr key={cr.id}>
                        <td style={styles.taskDescription}>
                          <strong>{cr.reunion_titre || cr.reunion?.titre || 'Sans titre'}</strong>
                          <div style={styles.taskMeta}>
                            {cr.resume?.substring(0, 50)}...
                          </div>
                        </td>
                        <td style={styles.td}>{cr.reunion_titre || cr.reunion?.titre || 'N/A'}</td>
                        <td style={styles.td}>{new Date(cr.created_at).toLocaleDateString('fr-FR')}</td>
                        <td style={styles.td}>{cr.auteur_nom || cr.auteur?.name || 'N/A'}</td>
                        <td style={styles.td}>
                          <span style={{
                            ...styles.statusBadge,
                            backgroundColor: status.bg,
                            color: status.color
                          }}>
                            {status.icon}
                            {status.text}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <div style={styles.actions}>
                            <button
                              onClick={() => handleView(cr.id)}
                              style={styles.viewButton}
                              title="Voir"
                            >
                              <Eye size={14} />
                            </button>
                            {cr.statut === 'brouillon' && (
                              <button
                                onClick={() => navigate(`/comptes-rendus/${cr.id}/editer`)}
                                style={styles.editButton}
                                title="Modifier"
                              >
                                <Edit size={14} />
                              </button>
                            )}
                            <button
                              onClick={() => handleDownload(cr.id)}
                              style={styles.downloadButton}
                              title="Télécharger"
                            >
                              <Download size={14} />
                            </button>
                            <button
                              onClick={() => handleDelete(cr)}
                              style={styles.deleteButton}
                              title="Supprimer"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {showAddPopup && (
          <div style={styles.overlay}>
            <div style={styles.popup}>
              <div style={styles.popupHeader}>
                <h3 style={styles.popupTitle}>Uploader un compte rendu PDF</h3>
                <button onClick={() => setShowAddPopup(false)} style={styles.closeButton}>
                  <X size={20} />
                </button>
              </div>
              
              <div style={styles.popupContent}>
                {uploadSuccess && (
                  <div style={styles.successMessageSmall}>
                    <CheckCircle size={18} color="#10b981" />
                    <span>Compte rendu uploadé avec succès !</span>
                  </div>
                )}
                
                {uploadError && (
                  <div style={styles.errorMessageSmall}>
                    <AlertCircle size={18} color="#ef4444" />
                    <span>{uploadError}</span>
                  </div>
                )}
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Réunion *</label>
                  <select
                    style={styles.select}
                    value={selectedReunion}
                    onChange={(e) => setSelectedReunion(e.target.value)}
                    disabled={loadingReunions}
                  >
                    <option value="">
                      {loadingReunions ? "Chargement des réunions..." : "Sélectionner une réunion"}
                    </option>
                    {reunions.map(reunion => (
                      <option key={reunion.id} value={reunion.id}>
                        {reunion.titre} - {reunion.date_heure ? new Date(reunion.date_heure).toLocaleDateString('fr-FR') : 'Date inconnue'}
                      </option>
                    ))}
                  </select>
                  {reunions.length === 0 && !loadingReunions && (
                    <p style={styles.noDataMessage}>
                      Aucune réunion disponible. Créez d'abord une réunion.
                    </p>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Fichier PDF *</label>
                  <div style={styles.uploadArea}>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleFileChange}
                      style={styles.fileInput}
                      id="pdf-upload"
                    />
                    <label htmlFor="pdf-upload" style={styles.uploadLabel}>
                      {pdfFile ? (
                        <span style={styles.fileSelected}>
                          <FileText size={20} color="#5e17eb" />
                          {pdfFile.name}
                        </span>
                      ) : (
                        <span style={styles.uploadPlaceholder}>
                          <FileText size={24} color="#9ca3af" />
                          Cliquez pour sélectionner un fichier PDF
                        </span>
                      )}
                    </label>
                  </div>
                  {pdfFile && (
                    <p style={styles.fileInfo}>
                      Taille: {(pdfFile.size / 1024).toFixed(2)} Ko
                    </p>
                  )}
                </div>
              </div>

              <div style={styles.popupFooter}>
                <button onClick={() => setShowAddPopup(false)} style={styles.cancelButton}>
                  Annuler
                </button>
                <button
                  onClick={handleUploadCompteRendu}
                  style={styles.uploadButton}
                  disabled={uploading || !selectedReunion || !pdfFile}
                >
                  {uploading ? (
                    <>
                      <div style={styles.smallSpinner}></div>
                      Upload en cours...
                    </>
                  ) : (
                    'Uploader le compte rendu'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeletePopup && compteRenduToDelete && (
          <div style={styles.overlay}>
            <div style={styles.deletePopup}>
              <div style={styles.deleteIcon}>
                <AlertCircle size={40} color="#ef4444" />
              </div>
              <h3 style={styles.deleteTitle}>Confirmer la suppression</h3>
              <p style={styles.deleteMessage}>
                Êtes-vous sûr de vouloir supprimer définitivement le compte rendu de la réunion<br />
                <strong>"{compteRenduToDelete.reunion_titre || compteRenduToDelete.reunion?.titre}"</strong> ?
              </p>
              <p style={styles.deleteWarning}>
                Cette action est irréversible.
              </p>
              <div style={styles.deleteActions}>
                <button
                  onClick={() => setShowDeletePopup(false)}
                  style={styles.deleteCancelButton}
                  disabled={deleting}
                >
                  Annuler
                </button>
                <button
                  onClick={confirmDelete}
                  style={styles.deleteConfirmButton}
                  disabled={deleting}
                >
                  {deleting ? 'Suppression...' : 'Oui, supprimer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

const styles = {
  page: { 
    padding: "22px", 
    background: "#f7f6ff", 
    minHeight: "100vh", 
    borderRadius: "16px" 
  },
  title: {
    fontSize: "26px",
    color: "#1f1f2e",
    marginBottom: "4px",
    fontWeight: "500",
  },
  subtitle: {
    color: "#666",
    marginBottom: "18px",
    fontSize: "14px",
  },
  successMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 20px',
    background: '#d1fae5',
    border: '1px solid #a7f3d0',
    borderRadius: '8px',
    marginBottom: '20px',
    color: '#065f46'
  },
  errorMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 20px',
    background: '#fee2e2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    marginBottom: '20px',
    color: '#991b1b'
  },
  statsBar: {
    display: "flex",
    gap: "24px",
    background: "white",
    padding: "16px 24px",
    borderRadius: "12px",
    marginBottom: "18px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    flexWrap: "wrap"
  },
  statItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
  },
  statValue: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#1f1f2e"
  },
  statLabel: {
    fontSize: "12px",
    color: "#666",
    marginTop: "4px"
  },
  topBar: { 
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "center",
    marginBottom: "18px",
    gap: "12px",
    flexWrap: "wrap"
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "white",
    padding: "4px 12px",
    borderRadius: "8px",
    border: "1px solid #e5e7eb"
  },
  filterSelect: {
    padding: "8px",
    border: "none",
    background: "transparent",
    outline: "none",
    cursor: "pointer",
    fontSize: "14px",
    minWidth: "150px"
  },
  searchBox: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    background: "white",
    padding: "0 12px",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    minWidth: "250px"
  },
  searchInput: {
    flex: 1,
    padding: "10px",
    border: "none",
    outline: "none",
    fontSize: "14px",
    background: "transparent"
  },
  addBtn: {
    backgroundColor: "#5e17eb",
    color: "white",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 16px",
    borderRadius: "8px",
    cursor: "pointer",
    border: "none",
    fontSize: "14px",
    fontWeight: "500",
    whiteSpace: "nowrap"
  },
  retryButton: {
    marginLeft: 'auto',
    padding: '4px 12px',
    background: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px'
  },
  tableContainer: {
    background: "white",
    borderRadius: "12px",
    padding: "20px",
    overflowX: "auto"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px"
  },
  th: {
    textAlign: "left",
    padding: "12px",
    borderBottom: "2px solid #e5e7eb",
    fontWeight: "600",
    color: "#4b5563"
  },
  td: {
    padding: "12px",
    borderBottom: "1px solid #f3f4f6"
  },
  taskDescription: {
    fontWeight: "500",
    color: "#1f1f2e",
    padding: "12px",
    borderBottom: "1px solid #f3f4f6"
  },
  taskMeta: {
    fontSize: "11px",
    color: "#9ca3af",
    marginTop: "2px"
  },
  emptyRow: {
    textAlign: "center",
    padding: "40px",
    color: "#9ca3af"
  },
  loading: {
    textAlign: "center",
    padding: "40px",
    color: "#666"
  },
  statusBadge: {
    padding: "4px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "500",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    textTransform: "capitalize"
  },
  actions: {
    display: "flex",
    gap: "8px",
    justifyContent: "center"
  },
  viewButton: {
    color: "#2563eb",
    padding: "6px",
    borderRadius: "6px",
    border: "none",
    background: "#dbeafe",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  editButton: {
    color: "#f59e0b",
    padding: "6px",
    borderRadius: "6px",
    border: "none",
    background: "#fef3c7",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  downloadButton: {
    color: "#16a34a",
    padding: "6px",
    borderRadius: "6px",
    border: "none",
    background: "#e6f7e6",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  deleteButton: {
    color: "#ef4444",
    padding: "6px",
    borderRadius: "6px",
    border: "none",
    background: "#fee2e2",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)',
  },
  popup: {
    background: 'white',
    borderRadius: 16,
    width: '90%',
    maxWidth: 500,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  popupHeader: {
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  popupTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: '#1f1f2e',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#666',
    padding: 4,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  popupContent: {
    padding: '24px',
  },
  successMessageSmall: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px',
    background: '#d1fae5',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '13px',
    color: '#065f46'
  },
  errorMessageSmall: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px',
    background: '#fee2e2',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '13px',
    color: '#991b1b'
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    display: 'block',
    fontSize: 14,
    fontWeight: 500,
    color: '#4b5563',
    marginBottom: 8,
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    fontSize: 14,
  },
  noDataMessage: {
    fontSize: 12,
    color: '#ef4444',
    marginTop: 4,
  },
  uploadArea: {
    border: '2px dashed #e5e7eb',
    borderRadius: 8,
    padding: '20px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  fileInput: {
    display: 'none',
  },
  uploadLabel: {
    display: 'block',
    cursor: 'pointer',
  },
  uploadPlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    color: '#9ca3af',
  },
  fileSelected: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#1f1f2e',
    fontSize: 14,
  },
  fileInfo: {
    fontSize: 12,
    color: '#16a34a',
    marginTop: 4,
  },
  smallSpinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginRight: '8px',
    display: 'inline-block'
  },
  popupFooter: {
    padding: '20px 24px',
    borderTop: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
  },
  cancelButton: {
    padding: '8px 16px',
    background: '#f3f4f6',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    color: '#666',
    cursor: 'pointer',
  },
  uploadButton: {
    padding: '8px 20px',
    background: '#5e17eb',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  deletePopup: {
    background: 'white',
    borderRadius: 16,
    padding: 32,
    width: '90%',
    maxWidth: 450,
    textAlign: 'center',
  },
  deleteIcon: {
    marginBottom: 16,
  },
  deleteTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: '#1f1f2e',
    marginBottom: 12,
  },
  deleteMessage: {
    fontSize: 14,
    color: '#4b5563',
    marginBottom: 8,
    lineHeight: 1.5,
  },
  deleteWarning: {
    fontSize: 13,
    color: '#ef4444',
    background: '#fee2e2',
    padding: 10,
    borderRadius: 8,
    marginBottom: 20,
  },
  deleteActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: 12,
  },
  deleteCancelButton: {
    padding: '10px 20px',
    background: '#f3f4f6',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    color: '#666',
    cursor: 'pointer',
  },
  deleteConfirmButton: {
    padding: '10px 20px',
    background: '#ef4444',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    color: 'white',
    cursor: 'pointer',
  },
};

const styleSheet = document.createElement("style");
styleSheet.innerText = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);